import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import { Button, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const ConfirmationDlgBox = (props) => {
  const { isButtonDisabled, loading } = props;
  const { t } = useTranslation();
  const [isThirdActionRunning, setIsThirdActionRunning] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const isLoading = loading || isConfirming;

  const handleClose = () => {
    if (isLoading || isThirdActionRunning) return;
    props?.setOpen(false);
    !!props?.setCustomerId && props?.setCustomerId(null);
    !!props?.setIsSubmitted && props?.setIsSubmitted(false);
    !!props?.cancelFunction && props?.cancelFunction();
  };
  const handleConfirm = async () => {
    if (isLoading || isThirdActionRunning) return;
    setIsConfirming(true);
    let keepOpen = false;
    try {
      if (props?.confirmFunction) {
        keepOpen = (await props.confirmFunction()) === false;
      }
    } catch (err) {
    } finally {
      setIsConfirming(false);
      if (!keepOpen) props?.setOpen(false);
    }
  };
  const handleThirdAction = async () => {
    if (!props?.thirdActionFunction || isLoading || isThirdActionRunning) return;
    setIsThirdActionRunning(true);
    try {
      await props.thirdActionFunction();
    } finally {
      setIsThirdActionRunning(false);
    }
  };
  const [isYesButtonDisabled, setIsYesButtonDisabled] = useState(
    isButtonDisabled || false
  );

  const hasThirdAction = !!props?.thirdActionLabel;

  return (
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={props?.open}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={props?.open}>
          <Box
            sx={{
              width: { xs: "90vw", sm: "64vw", md: "42vw" },
              borderRadius: { xs: "15px", sm: "20px" },
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              outline: "none",
              p: { xs: 3, sm: 4 },
            }}
          >
            <Box>
              <Typography
                component="div"
                sx={{
                  fontWeight: 500,
                  justifyContent: "center",
                  fontSize: { xs: "3.59vw", sm: "1.918vw", md: "1.389vw" },
                  whiteSpace: "pre-line",
                  mb: hasThirdAction ? 3 : 0,
                  color: "#3B3B3B",
                  background: "none",
                  WebkitTextFillColor: "unset",
                  WebkitBackgroundClip: "unset",
                  ...(props?.labelSx || {}),
                }}
              >
                {props?.label}
              </Typography>

              {hasThirdAction ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    width: { xs: "85%", sm: "70%" },
                    margin: "0 auto",
                  }}
                >
                  <Button
                    onClick={handleThirdAction}
                    disabled={isThirdActionRunning || isLoading}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.2,
                      fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.111vw" },
                      background: "linear-gradient(90deg, #F7A86E 0%, #FF6B6B 100%)",
                      boxShadow: "none",
                    }}
                  >
                    {isThirdActionRunning && (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    )}
                    {props.thirdActionLabel}
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={isYesButtonDisabled || isLoading || isThirdActionRunning}
                    variant="outlined"
                    color="error"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.2,
                      whiteSpace:"nowrap",
                      fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.111vw" },
                    }}
                  >
                    {isLoading && (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    )}
                    {props?.confirmLabel || t("pages.logout.yes")}
                  </Button>
                  {!props?.hideCancelButton && (
                    <Button
                      onClick={handleClose}
                      disabled={isLoading || isThirdActionRunning}
                      variant="text"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.111vw" },
                      }}
                    >
                      {props?.cancelLabel || t("pages.logout.no")}
                    </Button>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                    margin: "6% auto 0 auto",
                    gap: 1.5,
                  }}
                >
                  {!props?.hideCancelButton && (
                    <Button
                      onClick={handleClose}
                      disabled={isLoading || isThirdActionRunning}
                      variant="outlined"
                      sx={{
                        flex: "0 1 auto",
                        minWidth: { xs: 110, md: 130 },
                        px: 2.5,
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        fontSize: { xs: "3.08vw", sm: "1.6vw", md: "0.95vw" },
                      }}
                    >
                      {props?.cancelLabel || t("pages.logout.no")}
                    </Button>
                  )}
                  <Button
                    disabled={isYesButtonDisabled || isLoading || isThirdActionRunning}
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                      flex: "0 1 auto",
                      minWidth: { xs: 110, md: 130 },
                      px: 2.5,
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      fontSize: { xs: "3.08vw", sm: "1.6vw", md: "0.95vw" },
                    }}
                  >
                    {isLoading && (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    )}
                    {props?.confirmLabel || t("pages.logout.yes")}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
  );
};

export default ConfirmationDlgBox;
